/**
 * Naive tools module
 */
import SugarDate from 'sugar';
import { randomBytes } from 'tweetnacl'; // npm i tweetnacl #see https://github.com/dchest/tweetnacl-js
import { encodeBase64, decodeBase64 } from 'tweetnacl-util'; // npm i tweetnacl-util
import { AxiosError } from 'axios'; // npm i axios #see https://github.com/axios/axios

import { words } from "./english";
import { getRequest, postRequest } from './genericRequests';
import { FORBIDDEN, UNAUTHORIZED } from './httpStatusCodes';

/**
 * Read the auth cookie
 * @returns {null | any} the JSON authorization session in Promise
 */
export function readCookie(): null | any {
    try {
        const cookies = document.cookie.split(';'); // returns [''] if no cookie, never null
        const authCookie = cookies.filter((x: string) => x.trim().startsWith("auth="))[0];
        if (authCookie) return JSON.parse(authCookie.split('=')[1] || null);
    } catch (err) {
        // no valid cookie (parse error)
        logError(err, "tools.ts:readCookie(): " + document?.cookie?.split(';')?.toString());
        clearCookie();
    }
    return null;
}

/**
 * Clear the auth cookie
 */
export function clearCookie() {
    // this.delete_cookie("auth");
    document.cookie = "auth=;max-age=0;expires=Thu, 01 Jan 1970 00:00:01 GMT";
}

/**
 * Encode an URI parameters
 * @param {object} params the parameters object {key1: val1, key2: val2...}
 * @returns {string} the encoded URI "?key1=val1&key2=val2..."
 */
export function encodeURIParams(params = {}): string {
    const keys: string[] = Object.keys(params);
    if (keys.length === 0) return '';
    return '?' + (keys.map((key) => [key, params[key]].map(encodeURIComponent).join('=')).join('&'));
}

/**
 * Encode (sanitize) a string with HTML entities
 * @param {string} s the string to sanitize
 * @returns {string} the HTML-encoded string
 */
export function encodeHtml(s: string): string {
    return s?.replace(/[\u00A0-\u9999<>&]/g, (i) => '&#' + i.charCodeAt(0) + ';');
}

/**
 * Open an external link
 * @param url the URL to open
 * @param newtab if the URL is opened in a new tab
 */
export function openLink(url: string, newtab = false) {
    window.open(url, newtab ? '_blank' : '_self');
    if (!newtab) window.location.reload();
}

/**
 * Truncates a string if is exceeds a given max. length and appends '...'
 * @param {string} s the string to truncate
 * @param {number} max the maximal length
 * @returns {string} the truncated string
 */
export function truncate(s: string, max: number): string {
    if (!s) return '';
    return s.length > max ? s.substring(0, max) + '...' : s;
}

/**
 * Get current datetime string
 * @param {any} date the date to convert (cf. doc Sugar.Date(date))
 */
export function getTime(date: string | Date | number | null | undefined = 'now', format = '%Y-%m-%d %H:%M:%S') {
    return SugarDate.Date(date).format(format).raw;
}

/**
 * Return duration as a String format
 * @param {int} duration the duration in minutes
 * @param {boolean} compact if formated as HH:MM, else NN h NN min
 */
export function durationString(inputduration: number, compact = false) {
    let duration = inputduration;
    const minutesPerHour = 60;
    if (!duration) return compact ? "0" : "";
    const minus = duration < 0;
    if (minus) duration = -duration;

    const minutes = duration % minutesPerHour;
    duration = duration - minutes;
    if (compact) {
        return (minus ? "-" : "") + Math.round(duration / minutesPerHour) + ":" +
            Math.round(minutes).toString().padStart(2, '0');
    }
    let returnStr = "";
    const hours = duration / minutesPerHour;
    if (hours > 0) returnStr += Math.round(hours) + " h ";
    if (minutes > 0) returnStr += Math.round(minutes) + " min ";
    return (minus ? "-" : "") + returnStr.trimEnd();
}


export function clientDate(date = 'now') { // DB-insert purpose, format for local date
    if (!date) return null;
    return getTime(date, '{yyyy}-{MM}-{dd} {HH}:{mm}:{ss}');
}

export function correctDateTZ(date: Date, add = false) {
    return date && date.setMinutes(date.getMinutes() + (add ? 1 : -1) * date.getTimezoneOffset());
}

/**
 * Check whether a string is NOT a valid UUID
 * @param {string} s the string to test
 */
export function isInvalidUUID(s: string) {
    return !/^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/.test(s);
}

/**
 * Simulate a link click to download the string into a file
 * @param {string} content the file content
 * @param {string} name the file name
 * @param {string} type the file content-type, ex. 'text/csv'
 */
export function downloadFile(content: string, name: string, type: string) {
    const element = document.createElement("a");
    element.href = URL.createObjectURL(new Blob([content], { type }));
    element.download = name;
    document.body.appendChild(element); // required for this to work in FireFox
    element.click();
}

/**
 * Download a file content XLS format (XML extension)
 * @param {string} content the file content
 * @param name the file name without extention
 */
export function downloadXLSFile(content: string, name: string) {
    window.alert('The Excel file will be downloded; please open it from Excel "XML" type');
    downloadFile(content, name + '.xml', 'text/xml');  // custom extension
}

/**
 * Download a file content CSV format (TXT extension)
 * @param {string} content the file content
 * @param name the file name without extention
 */
export function downloadCSVFile(content: string, name: string) {
    window.alert('The CSV file will be downloded; please open it from Excel "text" type');
    downloadFile(content, name + '.txt', 'text/txt');  // custom extension
}

/**
 * Download a file content HTML format
 * @param {string} content the file content
 * @param name the file name without extention
 */
export function downloadHTMLFile(content: string, name: string) {
    window.alert('The HTML file will be downloded');
    downloadFile(content, name + '.html', 'text/html');  // custom extension
}

/**
 * Download a file content JSON format
 * @param {string} content the file content
 * @param name the file name without extention
 */
export function downloadJSONFile(content: string, name: string) {
    window.alert('The JSON file will be downloded');
    downloadFile(content, name + '.json', 'text/json');  // custom extension
}

/**
 * Set localization for Germany
 * @returns the localization object, to be used in jqxGrid
 */
export function getLocalization(): any {
    return {
        AM: null,
        PM: null,
        firstDay: 1,
        percentSymbol: '%',
        currencySymbol: '€',
        currencySymbolPosition: 'after',
        decimalSeparator: ',',
        thousandsSeparator: '.',
        patterns: {
            D: 'dddd, d. MMMM yyyy',
            F: 'dddd, d. MMMM yyyy HH:mm:ss',
            M: 'dd MMMM',
            T: 'HH:mm:ss',
            Y: 'MMMM yyyy',
            d: 'dd.MM.yyyy',
            f: 'dddd, d. MMMM yyyy HH:mm',
            t: 'HH:mm'
        }
    };
}

/**
 * Encode a file size into B, KB, MB
 * @param {number} size the file size in bytes
 * @returns {string} the encoded file size
 */
export function encodeFileSize(size: number | null): string {
    if (size === null) return '?\xa0B';

    const KB = 1024, MB = KB * KB;
    const opt = { maximumFractionDigits: 2 };
    if (size >= MB) return (size / MB).toLocaleString('de-DE', opt) + '\xa0MB'; // non-breakable char: 0xa0
    if (size >= KB) return (size / KB).toLocaleString('de-DE', opt) + '\xa0KB';
    return size.toString() + '\xa0B';
}

/**
 * Simple and naive random integer generator [0, n-1]
 * @param {number} max the maximum integer (excluded)
 */
export function getRandomInt(max: number): number {
    return Math.floor(Math.random() * max);
}

/**
 * Generates a number sequence
 * @param len the sequence length
 * @param start the first element value
 * @param step the increment step
 * @returns the sequence as array
 */
export function sequence(len: number, start = 0, step = 1): Array<number> {
    return Array(len).fill(start).map((e, i) => e + i * step);
}

/**
 * Gets client windows width
 */
export function getWindowWidth() {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
}

/**
 * Logging an error
 */
export function logError(err, identifier) {
    const uri = "logging/errorLog";
    const data = {
        ERR_Identifier: identifier,
        ERR_Code: err?.status ?? (err?.response?.status ?? null),
        ERR_Message: err?.message ?? (err?.response?.message ?? null),
        ERR_Stack: err?.stack ?? (err?.response?.stack ?? null),
        original: err
    };

    postRequest(uri, data);
}

// //// Password fiedls utilities

// /**
//  * Generate a new password, copy it and paste it to new password field
//  * @param {boolean} isphrase if passphrase else password
//  */
// export function generateNew(txNewPasw: any, isphrase: boolean) {
//     if (!txNewPasw.val() || window.confirm('A password already exists. Generate a new one?')) {
//         document.getElementById("lbMessage").innerHTML =
//             `<i class="w3-text-blue">A good password was copied to the clipboard. Please enter it again and do not forget it!</i>`;
//         const pasw = isphrase ? generatePhrase(8) : generatePswd(9);
//         navigator.clipboard.writeText(pasw);
//         txNewPasw.val(pasw);
//     }
// }


//// TweetNacl cryptography functions and utilities

export function arrayToHex(arr) {
    return Array.from(arr, (i: number) => i.toString(16).padStart(2, "0")).join("");
}
export function arrayToB64(arr) {
    return encodeBase64(arr);
}
export function b64ToArray(arr) {
    return decodeBase64(arr);
}

/**
 * Generate a secure password by tweetnacl
 * @param {number} l the password length in bytes
 * @returns the password
 */
export function generatePswd(l: number = 9) {
    if (l < 2) throw new Error("Bytes length should be 2 minimum");

    return encodeBase64(randomBytes(l));
}

/**
 * Generate a secure passphrase by tweetnacl using 1626 common english words
 * Adapted from Session passphrase https://github.com/oxen-io/session-desktop
 * @param {number} l the phrase length in bytes
 * @returns the passphase
 */
export function generatePhrase(l: number = 8) {
    if (l < 2) throw new Error("Bytes length should be 2 minimum");

    const wordsCount = Math.ceil(8 * l / Math.log2(words.length));
    const bytes = randomBytes(l), result = Array(wordsCount);
    let sum = 0;

    // Base conversion 256 > words.length (1626)
    for (let i = 0; i < bytes.length; i++) {
        sum += Math.pow(256, i) * bytes[i];
    }
    for (let i = 0; i < wordsCount; i++) {
        result[i] = sum % words.length;
        sum = Math.floor(sum / words.length);
    }
    return result.map((i: number) => words[i]).join(" ");
}


/**
 * Capitalize a sentence string
 * @param str the sentence string
 * @returns the capitalized sentence
 */
export function toCapSentence(str: string) {
    if (!str) return str;
    return str.trim().replace(/\s+/g, ' ').split(' ').map((w: string) => capitalize(w)).join(" ");
}

/**
 * Capitalize a string (name)
 * @param str the string
 * @returns the capitalized string
 */
export function capitalize(str: string) {
    if (!str) return str;
    return str.trim().replace(/[A-Za-zÀ-ÖØ-öø-ÿ]+/g, (c) => c[0].toUpperCase() + c.substring(1).toLowerCase());
}


//// End of cryptography functions and utilities


export function isValidDate(datetime) {
    return !isNaN(Date.parse(datetime));
}

export function isAuthorizationStatus(status: number) {
    return [UNAUTHORIZED, FORBIDDEN].includes(status);
}

export function isAuthorizationError(err: AxiosError) {
    return isAuthorizationStatus(err?.status ?? err?.response?.status);
}

/**
 * HTTP Get session information from API
 *
 * Route: GET /api/sessions/get
 */
export async function httpLoadSession(authErrorCount: number, setAuthErrorCount: (count: number) => void) {
    return await getRequest("sessions/get").then((auth) => {
        setAuthErrorCount(0);

        return auth;
    }).catch(err => {
        setAuthErrorCount(authErrorCount + 1);

        throw err;
    });
}